import { useCallback, useState, useMemo } from 'react';
import type { AnalysisResult, AnalysisCategory, ApiClient } from '../types';

type SubmitArgs = {
  selectedCategory: string;
  inputText: string;
  selectedFiles: File[];
  formData: Record<string, string | boolean>;
};

export default function useAnalysis(apiClient: ApiClient) {
  const [availableCategories, setAvailableCategories] = useState<Record<string, AnalysisCategory>>({});
  const [analysisHistory, setAnalysisHistory] = useState<AnalysisResult[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('medical_image_analysis');
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  const categoryConfig = useMemo(() => availableCategories?.[selectedCategory], [availableCategories, selectedCategory]);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await apiClient.getCategories();
      setAvailableCategories(data.categories);
    } catch (e) {
      console.error('Failed to fetch categories:', e);
      setAvailableCategories({});
    }
  }, [apiClient]);

  const getContextFieldName = useCallback((categoryKey: string): string | null => {
    const mapping: Record<string, string | null> = {
      medical_image_analysis: 'user_context',
      audio_transcription: 'user_context',
      video_transcription: 'user_context',
      scene_ai_analysis: 'user_context',
    };
    return mapping[categoryKey] ?? 'user_context';
  }, []);

  const submit = useCallback(async ({ selectedCategory, inputText, selectedFiles, formData }: SubmitArgs) => {
    setLoading(true);
    setError(null);
    setLoadingMessage('Analyzing your request...');
    try {
      // Require property address for scene AI analysis
      if (selectedCategory === 'scene_ai_analysis') {
        const address =
          (formData && (formData.property_address || formData.propertyAddress || formData.address)) ||
          (inputText && inputText.trim());
        if (!address || String(address).trim().length === 0) {
          const msg = 'Property address is required for scene analysis.';
          setError(msg);
          setLoading(false);
          setLoadingMessage('');
          throw new Error(msg);
        }
      }
      const fd = new FormData();
      fd.append('analysis_category', selectedCategory);
      const contextFieldName = getContextFieldName(selectedCategory);
      if (inputText && inputText.trim() && contextFieldName) fd.append(contextFieldName, inputText.trim());

      if (selectedFiles && selectedFiles.length > 0) {
        selectedFiles.forEach((f) => fd.append('files', f));
      }

      Object.entries(formData || {}).forEach(([k, v]) => fd.append(k, String(v)));

      const data = await apiClient.analyzeFiles(fd);

      // Augment with session-only object URL if a file was uploaded
      let augmented = data as AnalysisResult & { fileUrl?: string };
      if (selectedFiles && selectedFiles.length > 0) {
        const f = selectedFiles[0];
        try {
          const obj = URL.createObjectURL(f);
          augmented = { ...data, fileUrl: obj } as typeof augmented;
        } catch (e) {
          // ignore
        }
      }

      setAnalysisHistory((prev) => [augmented, ...prev]);
      return augmented;
    } catch (e) {
      console.error('Analysis failed', e);
      setError(e instanceof Error ? e.message : String(e));
      throw e;
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }, [apiClient, getContextFieldName]);

  const updateHistoryChat = useCallback((item: AnalysisResult, chat: Array<{ role: string; content: string }>) => {
    setAnalysisHistory((prev) => {
      const idx = prev.findIndex((p) => (p.timestamp && item.timestamp && p.timestamp === item.timestamp) || (p.fileUrl && item.fileUrl && p.fileUrl === item.fileUrl));
      if (idx === -1) return prev;
      const copy = [...prev];
      copy[idx] = { ...copy[idx], chat };
      return copy;
    });
  }, []);

  return {
    availableCategories,
    fetchCategories,
    analysisHistory,
    submit,
    updateHistoryChat,
    loading,
    loadingMessage,
    error,
    setError,
    selectedCategory,
    setSelectedCategory,
    categoryConfig,
  };
}
